/**
 * @coeldery/song-card — 歌曲卡（標題／情緒標籤／狀態／歌詞／播放）
 *
 * 為何要模組：歌曲卡係「我嘅歌」同對話頁都要用嘅表面（第二個 app 都會用）→ 依 R3 做成模組。
 *
 * 規格：
 *   · 標題 ＋ 情緒標籤（chip）＋ 狀態（`lyrics_ready`／`succeeded`／`failed`…**照實顯示**）
 *   · 歌詞用 `pre-wrap` 顯示（保留換行）；長歌詞可摺疊（`collapsible`）
 *   · 有 `audioSrc` 才顯示播放器（1c 未有音頻 → 唔會出現假播放器 ✗ `rules.md` B2）
 *   · 錯誤：`error` 有值就**原樣顯示**（`ui-spec` §六：唔准吞 provider 錯誤）
 *   · 文字一律 i18n key；顏色只用 CSS var
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface SongCardProps {
  title: string;
  lyrics: string;
  moodTags?: string[];
  /** 狀態機值（原樣顯示，唔准美化） */
  status?: string;
  /** provider 錯誤原文（有就顯示） */
  error?: string | null;
  /** 有音頻才傳（R2 串流 URL）；冇 → 唔顯示播放器 */
  audioSrc?: string | null;
  /** 長歌詞摺疊 */
  collapsible?: boolean;
  /** 標題右側動作（例：分享） */
  action?: React.ReactNode;
}

export default function SongCard({
  title,
  lyrics,
  moodTags = [],
  status,
  error,
  audioSrc,
  collapsible = true,
  action,
}: SongCardProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(!collapsible);
  const lines = lyrics.split('\n').filter((l) => l.trim() !== '');
  const preview = lines.slice(0, 4).join('\n');

  return (
    <article
      style={{
        backgroundColor: 'var(--color-card)',
        border: '1px solid var(--color-divider)',
        borderRadius: '14px',
        padding: '14px 16px',
        boxShadow: 'var(--shadow-soft)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
        <h3 style={{ margin: 0, fontSize: '17px', color: 'var(--color-text)' }}>{title}</h3>
        {action}
      </header>

      {moodTags.length > 0 ? (
        <ul style={{ listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '10px 0 0', padding: 0 }}>
          {moodTags.map((tag) => (
            <li
              key={tag}
              style={{
                fontSize: 'var(--font-size-tiny, 13.5px)',
                fontWeight: 700,
                padding: '2px 10px',
                borderRadius: '999px',
                backgroundColor: 'var(--color-divider)',
                color: 'var(--color-text)',
              }}
            >
              {tag}
            </li>
          ))}
        </ul>
      ) : null}

      <pre
        style={{
          margin: '12px 0 0',
          whiteSpace: 'pre-wrap',
          fontFamily: 'inherit',
          fontSize: 'var(--font-size-button, 17px)',
          lineHeight: 1.7,
          color: 'var(--color-text)',
        }}
      >
        {open ? lyrics : `${preview}${lines.length > 4 ? '…' : ''}`}
      </pre>

      {collapsible && lines.length > 4 ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            marginTop: '8px',
            minHeight: '44px',
            padding: '0 14px',
            border: '1px solid var(--color-divider)',
            borderRadius: '22px',
            background: 'none',
            color: 'var(--color-text-secondary)',
            fontFamily: 'inherit',
            fontSize: 'var(--font-size-small, 14px)',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {t('song_toggle')}
        </button>
      ) : null}

      {audioSrc ? (
        <audio controls src={audioSrc} style={{ width: '100%', marginTop: '12px' }} />
      ) : null}

      {status ? (
        <p style={{ margin: '10px 0 0', fontSize: 'var(--font-size-tiny, 13.5px)', color: 'var(--color-text-secondary)' }}>
          {t('song_status_label')}：{status}
        </p>
      ) : null}

      {error ? (
        <p role="alert" style={{ margin: '8px 0 0', fontSize: 'var(--font-size-small, 14px)', color: 'var(--color-accent)' }}>
          {error}
        </p>
      ) : null}
    </article>
  );
}
